/**
 * MyMouvSuccess — Stripe checkout redirect target. Confirms payment then routes
 * the buyer to /mymouv/my-tickets.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

const MyMouvSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('checking'); // checking | ok | error
  const [error, setError] = useState('');

  useEffect(() => {
    const ids = (params.get('ticket_ids') || '').split(',').filter(Boolean);
    if (ids.length === 0) { setState('error'); setError('Aucun billet à confirmer'); return; }
    (async () => {
      try {
        // Confirm all (idempotent — one call confirms sibling tickets too)
        await axios.post(`/api/mymouv/tickets/${ids[0]}/confirm-payment`);
        setState('ok');
        setTimeout(() => navigate('/mymouv/my-tickets', { replace: true }), 2000);
      } catch (e) {
        setError(e.response?.data?.detail || 'Erreur de confirmation');
        setState('error');
      }
    })();
  }, [params, navigate]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4" data-testid="mymouv-success">
      <div className="max-w-md w-full text-center p-8 rounded-3xl bg-white/5 border border-white/10">
        {state === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-fuchsia-400" />
            <h1 className="text-xl font-bold mt-4">Confirmation du paiement…</h1>
            <p className="text-sm text-white/60 mt-2">Ne fermez pas cette page.</p>
          </>
        )}
        {state === 'ok' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400" />
            <h1 className="text-2xl font-bold mt-4">Paiement confirmé !</h1>
            <p className="text-sm text-white/60 mt-2">Vos billets sont disponibles dans votre profil. Un email vient de vous être envoyé.</p>
            <button onClick={() => navigate('/mymouv/my-tickets')} className="mt-6 w-full py-3 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-600 font-semibold" data-testid="mymouv-success-goto-tickets">Voir mes billets</button>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-red-400" />
            <h1 className="text-2xl font-bold mt-4">Une erreur est survenue</h1>
            <p className="text-sm text-white/60 mt-2">{error}</p>
            <button onClick={() => navigate('/mymouv')} className="mt-6 w-full py-3 rounded-xl bg-white/10 hover:bg-white/20">Retour</button>
          </>
        )}
      </div>
    </div>
  );
};

export default MyMouvSuccess;
